import { CircleCheck, CircleX } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useMutation } from '@tanstack/react-query';
import { Logo } from '@/components/brand/Logo';
import { Button, Field, Input } from '@/components/ui';
import { api, isApiError } from '@/lib/api';
import styles from '@/pages/ops/mc.module.css';
import parity from '@/pages/ops/parity.module.css';

const MIN = 12;

function Shell({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <main className={styles.vpage}>
      <div className={styles.vwrap}>
        <div className={styles.vtop}>
          <Logo />
          <span>{label}</span>
        </div>
        {children}
      </div>
    </main>
  );
}

/** RL-1788: "Forgot password?" for staff (the same answer for any email). */
export function StaffForgotPasswordPage() {
  const [email, setEmail] = useState('');
  useEffect(() => {
    document.title = 'Forgot password · Ommri Care';
  }, []);
  const ask = useMutation({
    mutationFn: () =>
      api
        .post<{ accepted: true; message: string }>('/auth/password-reset', {
          email: email.trim(),
        })
        .then((r) => r.data),
  });
  return (
    <Shell label="Forgot password">
      {ask.isSuccess ? (
        <div className={`${styles.vban} ${styles.ok}`} role="status">
          <CircleCheck aria-hidden="true" />
          <div>
            <b>Check your email</b>
            <span>
              {ask.data.message} <Link to="/sign-in">Back to sign in</Link>
            </span>
          </div>
        </div>
      ) : (
        <form
          className={parity.form}
          onSubmit={(e) => {
            e.preventDefault();
            ask.mutate();
          }}
        >
          <p>
            Enter the email you sign in with. If it belongs to a staff account,
            we email a link to choose a new password.
          </p>
          <Field label="Email">
            <Input
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Field>
          {ask.error ? (
            <p className={parity.notice} role="alert">
              {isApiError(ask.error) && ask.error.status === 429
                ? 'Too many tries. Wait a minute and try again.'
                : 'Could not send the link. Try again.'}
            </p>
          ) : null}
          <Button
            type="submit"
            variant="primary"
            loading={ask.isPending}
            disabled={!/^\S+@\S+\.\S+$/.test(email.trim())}
          >
            Email me a link
          </Button>
          <Link to="/sign-in">Back to sign in</Link>
        </form>
      )}
    </Shell>
  );
}

/**
 * RL-1788: choose a new staff password from the emailed link. The token is
 * in the URL fragment (never sent to a server) and posted in the body.
 */
export function StaffResetPasswordPage() {
  const [token] = useState(() => window.location.hash.replace(/^#/, ''));
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  useEffect(() => {
    document.title = 'Choose a new password · Ommri Care';
    if (window.location.hash)
      window.history.replaceState(null, '', window.location.pathname);
  }, []);
  const reset = useMutation({
    mutationFn: () =>
      api
        .post<{ reset: true }>('/auth/password-reset/complete', {
          token,
          password,
        })
        .then((r) => r.data),
  });
  const badLink =
    !/^[A-Za-z0-9_-]{30,64}$/.test(token) ||
    (isApiError(reset.error) &&
      (reset.error.status === 410 || reset.error.status === 400));
  const mismatch = confirm.length > 0 && confirm !== password;

  let body;
  if (reset.isSuccess) {
    body = (
      <div className={`${styles.vban} ${styles.ok}`} role="status">
        <CircleCheck aria-hidden="true" />
        <div>
          <b>Your password is changed</b>
          <span>
            You were signed out everywhere. Sign in with the new password and
            your two-factor code. <Link to="/sign-in">Go to sign in</Link>
          </span>
        </div>
      </div>
    );
  } else if (badLink) {
    body = (
      <div className={`${styles.vban} ${styles.bad}`} role="alert">
        <CircleX aria-hidden="true" />
        <div>
          <b>This reset link cannot be used</b>
          <span>
            It may be older than one hour, already used, or replaced by a newer
            one. <Link to="/forgot-password">Ask for a new link</Link>
          </span>
        </div>
      </div>
    );
  } else {
    body = (
      <form
        className={parity.form}
        onSubmit={(e) => {
          e.preventDefault();
          reset.mutate();
        }}
      >
        <Field
          label="New password"
          hint={`At least ${MIN} characters, not your email name.`}
        >
          <Input
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        <Field
          label="Confirm password"
          error={mismatch ? 'The passwords do not match.' : undefined}
        >
          <Input
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </Field>
        {reset.error ? (
          <p className={parity.notice} role="alert">
            {isApiError(reset.error)
              ? (reset.error.problem?.detail ?? reset.error.message)
              : 'Could not change the password.'}
          </p>
        ) : null}
        <Button
          type="submit"
          variant="primary"
          loading={reset.isPending}
          disabled={password.length < MIN || confirm !== password}
        >
          Save new password
        </Button>
      </form>
    );
  }
  return <Shell label="Choose a new password">{body}</Shell>;
}
